// The horizontal layout shared by the syllable blocks, the beat numbers and the notation row
// (issue #3473). Every row is one CSS grid of sixteenth columns with the block gap between each
// column, so an event spanning k sixteenths is k columns plus the k - 1 gaps inside it: widths stay
// proportional to duration, the gaps between events are uniform, and a note in the notation row
// lands under its syllable without anyone measuring pixels.

import styled from 'styled-components';
import { rhythmBeatsPerBar, RhythmPattern } from '../../../../../common/src/rhythm_echo.ts';

export const rhythmBlockGap = 8;
const subdivisionsPerBeat = 4; // sixteenth resolution
export const rhythmGridColumns = rhythmBeatsPerBar * subdivisionsPerBeat;

export const RhythmRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${rhythmGridColumns}, minmax(0, 1fr));
  column-gap: ${rhythmBlockGap}px;
`;

// The grid-column value for a span starting `startBeats` into the bar and lasting `beats`
export function gridColumn(startBeats: number, beats: number): string {
  const start = Math.round(startBeats * subdivisionsPerBeat) + 1;
  const span = Math.max(1, Math.round(beats * subdivisionsPerBeat));
  return `${start} / span ${span}`;
}

// Where each event starts, in beats from the bar start
export function eventStartBeats(pattern: RhythmPattern): number[] {
  let position = 0;
  return pattern.map((event) => {
    const start = position;
    position += event.beats;
    return start;
  });
}

// The left edge of a point `beats` into the bar on a row's time axis, as a CSS length
// expression, for a mark positioned absolutely inside a full-width row (the tap notation row).
// Time runs across each event's cell exactly as RhythmNotation places notes at a fraction of
// their cell, so a ghost of a pattern note lands where the pattern row draws it and a tap 100 ms
// late sits the same distance to the right of its note whichever note it belongs to; the gaps
// between events take no time. Past the bar's end the axis keeps the last event's scale (a tap in
// the trailing band); the caller clamps what it draws to the row.
export function timeLeft(pattern: RhythmPattern, beats: number): string {
  const starts = eventStartBeats(pattern);
  const found = starts.findIndex((start, index) => beats < start + pattern[index].beats);
  const index = found === -1 ? pattern.length - 1 : found;
  const event = pattern[index];
  const start = starts[index];
  const columns = Math.round(start * subdivisionsPerBeat);
  const span = Math.max(1, Math.round(event.beats * subdivisionsPerBeat));
  const fraction = (beats - start) / event.beats;
  const columnWidth = `(100% - ${(rhythmGridColumns - 1) * rhythmBlockGap}px) / ${rhythmGridColumns}`;
  const cellLeft = `${columns} * (${columnWidth} + ${rhythmBlockGap}px)`;
  const cellWidth = `(${span} * ${columnWidth} + ${(span - 1) * rhythmBlockGap}px)`;
  return `calc(${cellLeft} + ${fraction} * ${cellWidth})`;
}
